/**
 * Core Reversi (Othello) Game Types
 *
 * All TypeScript types and interfaces for the Reversi game.
 * Board: 8×8 grid, 2 players (Black and White).
 * Standard Othello rules: outflank opponent discs to flip them.
 */

// ============================================================================
// Board Configuration
// ============================================================================

/** State of a single cell on the board */
export type CellState = 'empty' | 'black' | 'white';

/** Player color */
export type PlayerColor = 'black' | 'white';

/** Board position */
export interface Position {
  row: number;
  col: number;
}

/** A move in the game */
export interface ReversiMove {
  player: PlayerColor;
  position: Position;
  timestamp: number;
}

/** Result of executing a move */
export interface MoveResult {
  flippedDiscs: Position[];
  newBoard: CellState[][];
  validMoves: Position[];
  move: ReversiMove;
}

// ============================================================================
// Game State
// ============================================================================

export interface ReversiGameState {
  board: CellState[][];
  currentPlayer: PlayerColor;
  moveHistory: ReversiMove[];
  gameStatus: 'active' | 'completed';
  winner?: PlayerColor | 'draw';
  score: { black: number; white: number };
  validMoves: Position[];
  lastFlipped: Position[];
  gameId: string;
  startedAt: number;
  skippedTurns: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export type ReversiDifficulty = 'easy' | 'medium' | 'hard';

export interface ReversiGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: ReversiDifficulty;
  playerColor?: PlayerColor;
}

// ============================================================================
// Statistics
// ============================================================================

export interface ReversiGameStats {
  wins: number;
  losses: number;
  draws: number;
  winPercentage: number;
  averageScore: number;
  highestScore: number;
  totalDiscsFlipped: number;
}

export interface ReversiStatistics {
  userId?: string;
  singlePlayer: {
    easy: ReversiGameStats;
    medium: ReversiGameStats;
    hard: ReversiGameStats;
  };
  localMultiplayer: ReversiGameStats;
  onlineMultiplayer: ReversiGameStats;
  totalGames: number;
  totalPlayTime: number;
}

export interface ReversiGameResult {
  gameId: string;
  mode: ReversiGameSettings['mode'];
  difficulty?: ReversiDifficulty;
  winner?: PlayerColor | 'draw';
  score: { black: number; white: number };
  totalMoves: number;
  totalFlips: number;
  duration: number;
  completedAt: number;
}

// ============================================================================
// Component Props
// ============================================================================

export interface ReversiGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface ReversiGameModeSelectorProps {
  onStartGame: (settings: ReversiGameSettings) => void;
  onJoinGame?: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
  isOnline?: boolean;
}

export interface ReversiBoardProps {
  board: CellState[][];
  validMoves: Position[];
  lastFlipped: Position[];
  lastMove?: Position | null;
  currentPlayer: PlayerColor;
  onCellClick: (position: Position) => void;
  disabled?: boolean;
  animating?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

export const BOARD_SIZE = 8;

/** 8 directions for disc flipping: [row delta, col delta] */
export const DIRECTIONS: [number, number][] = [
  [-1, -1], // top-left
  [-1, 0],  // top
  [-1, 1],  // top-right
  [0, -1],  // left
  [0, 1],   // right
  [1, -1],  // bottom-left
  [1, 0],   // bottom
  [1, 1],   // bottom-right
];
